// Export dialog (§11.13): several presets at once, scope, destination (folder or zip), rename + dish
// naming, watermark, metadata; per-file progress with ETA and Cancel; summary with failures.
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLang, useT, type MessageKey } from '../../i18n';
import { useLibrary, type LibraryPhoto } from '../../state/library';
import { useEditor } from '../../state/editor';
import { listExportPresets, presetLabel, needsSpecCheck } from '../../features/exportPresets';
import { runBatch, type BatchResult, type Progress } from '../../features/batchExport';
import { loadLogo, loadMetadata, loadWatermark, saveLogo, saveWatermark } from '../../features/appSettings';
import type { WatermarkSettings } from '../../features/watermark';
import { buildContactSheet } from '../../features/contactSheet';
import { exportWithExtras } from '../../features/exportHelpers';
import { saveAs } from '../../io/fs';
import { assignDishNames } from '../../io/rename';
import type { ExportPreset } from '../../model/types';
import s from './Dialog.module.css';

const sel = {
  background: 'var(--bg)',
  color: 'var(--text)',
  border: '1px solid var(--line)',
  borderRadius: 3,
  padding: 3,
};
const POS: MessageKey[] = ['posTL', 'posTC', 'posTR', 'posML', 'posC', 'posMR', 'posBL', 'posBC', 'posBR'];

function Section({ title, children }: { title: MessageKey; children: ReactNode }) {
  const t = useT();
  return (
    <fieldset
      style={{ border: '1px solid var(--line)', borderRadius: 4, margin: '8px 0', padding: '6px 10px' }}
    >
      <legend style={{ padding: '0 4px', color: 'var(--muted)' }}>{t(title)}</legend>
      {children}
    </fieldset>
  );
}

export function ExportDialog({ onClose }: { onClose: () => void }) {
  const t = useT();
  const lang = useLang((st) => st.lang);
  const lib = useLibrary();
  const [presets, setPresets] = useState<ExportPreset[]>([]);
  const [chosen, setChosen] = useState<string[]>(['full-res-jpeg']);
  const [scope, setScope] = useState<'selected' | 'picks' | 'all'>(
    lib.selected.size > 1 ? 'selected' : 'all',
  );
  const fsa = lib.folder?.mode === 'fsa';
  const [dest, setDest] = useState<'folder' | 'zip'>(fsa ? 'folder' : 'zip');
  const [renameOn, setRenameOn] = useState(false);
  const [template, setTemplate] = useState('{orig}');
  const [client, setClient] = useState('');
  const [event, setEvent] = useState('');
  const [menu, setMenu] = useState('');
  const [dishEdits, setDishEdits] = useState<Record<number, string>>({});
  const [wmOn, setWmOn] = useState(false);
  const [sheetOn, setSheetOn] = useState(false);
  const [paper, setPaper] = useState<'a4' | 'letter'>('a4');
  const [wm, setWm] = useState<WatermarkSettings | null>(null);
  const [metaMode, setMetaMode] = useState<ExportPreset['metadata'] | ''>('');
  const [progress, setProgress] = useState<Progress | null>(null);
  const [result, setResult] = useState<BatchResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const signal = useRef({ cancelled: false });
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    void listExportPresets().then(setPresets);
    void Promise.all([loadWatermark(), loadLogo()]).then(([w, logo]) =>
      setWm({ ...w, ...(logo ? { logo } : {}) }),
    );
  }, []);

  const scoped: LibraryPhoto[] = useMemo(() => {
    if (scope === 'selected') return lib.photos.filter((p) => lib.selected.has(p.id));
    if (scope === 'picks') return lib.photos.filter((p) => !p.rejected);
    return lib.photos;
  }, [scope, lib.photos, lib.selected]);
  const dishes = useMemo(
    () => assignDishNames(menu, scoped.length).map((d, i) => dishEdits[i] ?? d),
    [menu, scoped.length, dishEdits],
  );

  const run = async () => {
    const ps = presets.filter((p) => chosen.includes(p.id));
    if (!ps.length) return setError(t('noPresetChosen'));
    setError(null);
    setResult(null);
    signal.current = { cancelled: false };
    setCancelling(false);
    if (wm) await saveWatermark(wm);
    const meta = await loadMetadata();
    const res = await runBatch({
      photos: scoped,
      presets: ps,
      destination: dest,
      folder: lib.folder,
      rename: { enabled: renameOn, template, client, event, dishNames: renameOn ? dishes : undefined },
      watermark: wm,
      watermarkAll: wmOn,
      metadataOverride: metaMode || null,
      meta,
      getSettings: (id) => useEditor.getState().get(id),
      exportOne: (file, settings, opts, m) => {
        const photo = lib.photos.find((p) => p.file === file);
        // A known photo exports from its upscaled source when upscale is on (M13).
        return exportWithExtras(photo, settings, opts, m, photo ? undefined : file);
      },
      contactSheet: sheetOn
        ? async (order) => {
            const items = [];
            for (const { photo, name } of order) {
              const s = useEditor.getState().get(photo.id);
              const r = await exportWithExtras(
                photo,
                s,
                {
                  format: 'jpeg',
                  bitDepth: 8,
                  quality: 80,
                  size: { mode: 'longEdge', w: 400 },
                  dpi: 72,
                  colorSpace: 'srgb',
                  sharpen: { target: 'none', amount: 'standard' },
                  background: 'asEdited',
                  metadata: 'stripAll',
                },
                {},
              );
              items.push({ jpeg: r.bytes, width: r.width, height: r.height, name, rating: photo.rating });
            }
            return buildContactSheet(items, {
              paper,
              client,
              event,
              date: new Date().toISOString().slice(0, 10),
              labels: {
                photos: (n) => t('contactPhotos', { n }),
                page: (n, total) => t('contactPage', { n, total }),
              },
            });
          }
        : undefined,
      onProgress: setProgress,
      signal: signal.current,
    });
    if (res.zip) await saveAs(res.zip, `${lib.folderName || 'export'}.zip`, 'application/zip');
    setResult(res);
  };

  const running = progress !== null && result === null;
  return (
    <div
      className={s.backdrop}
      role="dialog"
      aria-modal="true"
      aria-labelledby="export-title"
      onKeyDown={(e) => e.key === 'Escape' && !running && onClose()}
    >
      <div
        className={s.dialog}
        data-testid="export-dialog"
        style={{ maxWidth: 760, maxHeight: 'calc(100vh - 40px)', overflow: 'auto' }}
      >
        <h2 id="export-title">{t('export')}</h2>
        {!running && !result && (
          <>
            <Section title="exportPresets">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 12px' }}>
                {presets.map((p) => (
                  <label key={p.id} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      data-preset={p.id}
                      checked={chosen.includes(p.id)}
                      onChange={(e) =>
                        setChosen(e.target.checked ? [...chosen, p.id] : chosen.filter((x) => x !== p.id))
                      }
                    />
                    <span>
                      {presetLabel(p, lang)}
                      {needsSpecCheck(p) && (
                        <span style={{ color: 'var(--warn)', fontSize: 11 }}> · {t('specNote')}</span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
              {chosen.some((id) => presets.find((p) => p.id === id)?.format === 'jpeg') && (
                <p className={s.muted}>{t('jpegFlattenNote')}</p>
              )}
            </Section>
            <Section title="scope">
              {(['selected', 'picks', 'all'] as const).map((k) => (
                <label key={k} style={{ marginRight: 12 }}>
                  <input type="radio" name="scope" checked={scope === k} onChange={() => setScope(k)} />{' '}
                  {t(k === 'selected' ? 'scopeSelected' : k === 'picks' ? 'scopePicks' : 'scopeAll', {
                    n:
                      k === 'selected'
                        ? lib.selected.size
                        : k === 'picks'
                          ? lib.photos.filter((p) => !p.rejected).length
                          : lib.photos.length,
                  })}
                </label>
              ))}
            </Section>
            <Section title="destination">
              <label style={{ marginRight: 12 }}>
                <input
                  type="radio"
                  name="dest"
                  disabled={!fsa}
                  checked={dest === 'folder'}
                  onChange={() => setDest('folder')}
                />{' '}
                {t('destFolder')}
              </label>
              <label>
                <input type="radio" name="dest" checked={dest === 'zip'} onChange={() => setDest('zip')} />{' '}
                {t('destZip')}
              </label>
            </Section>
            <Section title="contactSheet">
              <label style={{ marginRight: 12 }}>
                <input
                  type="checkbox"
                  data-testid="contact-sheet"
                  checked={sheetOn}
                  onChange={(e) => setSheetOn(e.target.checked)}
                />{' '}
                {t('contactSheet')}
              </label>
              {sheetOn && (
                <label>
                  {t('contactPaper')}{' '}
                  <select value={paper} onChange={(e) => setPaper(e.target.value as 'a4' | 'letter')}>
                    <option value="a4">{t('contactA4')}</option>
                    <option value="letter">{t('contactLetter')}</option>
                  </select>
                </label>
              )}
            </Section>
            <Section title="rename">
              <label>
                <input
                  type="checkbox"
                  checked={renameOn}
                  onChange={(e) => setRenameOn(e.target.checked)}
                  data-testid="rename-on"
                />{' '}
                {t('rename')}
              </label>
              {renameOn && (
                <div style={{ marginTop: 6 }}>
                  <input
                    style={{ ...sel, width: '100%' }}
                    aria-label={t('renameTemplate')}
                    value={template}
                    onChange={(e) => setTemplate(e.target.value)}
                    data-testid="rename-template"
                  />
                  <div className={s.muted}>{t('renameTokens')}</div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                    <input
                      style={sel}
                      placeholder={t('client')}
                      aria-label={t('client')}
                      value={client}
                      onChange={(e) => setClient(e.target.value)}
                    />
                    <input
                      style={sel}
                      placeholder={t('event')}
                      aria-label={t('event')}
                      value={event}
                      onChange={(e) => setEvent(e.target.value)}
                    />
                  </div>
                  <textarea
                    style={{ ...sel, width: '100%', height: 70, marginTop: 6 }}
                    placeholder={t('dishNames')}
                    aria-label={t('dishNames')}
                    value={menu}
                    onChange={(e) => setMenu(e.target.value)}
                    data-testid="dish-names"
                  />
                  {menu.trim() && (
                    <table style={{ width: '100%', fontSize: 12 }} aria-label={t('dishPreview')}>
                      <tbody>
                        {scoped.slice(0, 50).map((p, i) => (
                          <tr key={p.id}>
                            <td style={{ color: 'var(--muted)' }}>{p.name}</td>
                            <td>
                              <input
                                style={{ ...sel, width: '100%' }}
                                value={dishes[i] ?? ''}
                                aria-label={p.name}
                                onChange={(e) => setDishEdits({ ...dishEdits, [i]: e.target.value })}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </Section>
            <Section title="watermark">
              <label>
                <input type="checkbox" checked={wmOn} onChange={(e) => setWmOn(e.target.checked)} />{' '}
                {t('watermark')}
              </label>
              {wmOn && wm && (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '180px 1fr',
                    gap: 4,
                    marginTop: 6,
                    alignItems: 'center',
                  }}
                >
                  <span>{t('wmText')}</span>
                  <span style={{ display: 'flex', gap: 6 }}>
                    <select
                      style={sel}
                      value={wm.kind}
                      onChange={(e) => setWm({ ...wm, kind: e.target.value as 'text' | 'logo' })}
                    >
                      <option value="text">{t('wmText')}</option>
                      <option value="logo">{t('wmLogo')}</option>
                    </select>
                    {wm.kind === 'text' ? (
                      <input
                        style={sel}
                        value={wm.text}
                        aria-label={t('wmText')}
                        onChange={(e) => setWm({ ...wm, text: e.target.value })}
                      />
                    ) : (
                      <button
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = 'image/png,image/webp'; // alpha kept (watermark.ts)
                          input.onchange = async () => {
                            const f = input.files?.[0];
                            if (!f) return;
                            const logo = new Uint8Array(await f.arrayBuffer());
                            await saveLogo(logo);
                            setWm((w) => (w ? { ...w, logo } : w));
                          };
                          input.click();
                        }}
                      >
                        {t('wmChooseLogo')}
                      </button>
                    )}
                  </span>
                  <span>{t('wmSize')}</span>
                  <input
                    type="number"
                    min={1}
                    max={50}
                    step={0.5}
                    style={sel}
                    value={wm.sizePct}
                    onChange={(e) => setWm({ ...wm, sizePct: Number(e.target.value) || 4 })}
                  />
                  <span>{t('wmOpacity')}</span>
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.05}
                    value={wm.opacity}
                    aria-label={t('wmOpacity')}
                    onChange={(e) => setWm({ ...wm, opacity: Number(e.target.value) })}
                  />
                  <span>{t('wmPosition')}</span>
                  <select
                    style={sel}
                    value={wm.position}
                    onChange={(e) => setWm({ ...wm, position: Number(e.target.value) })}
                  >
                    {POS.map((k, i) => (
                      <option key={k} value={i}>
                        {t(k)}
                      </option>
                    ))}
                  </select>
                  <span>{t('wmMargin')}</span>
                  <input
                    type="number"
                    min={0}
                    max={20}
                    step={0.5}
                    style={sel}
                    value={wm.marginPct}
                    onChange={(e) => setWm({ ...wm, marginPct: Number(e.target.value) || 0 })}
                  />
                  {wm.kind === 'text' && (
                    <>
                      <span>{t('wmColor')}</span>
                      <input
                        type="color"
                        aria-label={t('wmColor')}
                        value={
                          '#' +
                          wm.color
                            .map((v) =>
                              Math.round(v * 255)
                                .toString(16)
                                .padStart(2, '0'),
                            )
                            .join('')
                        }
                        onChange={(e) => {
                          const h = e.target.value;
                          setWm({
                            ...wm,
                            color: [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16) / 255) as [
                              number,
                              number,
                              number,
                            ],
                          });
                        }}
                      />
                    </>
                  )}
                </div>
              )}
            </Section>
            <Section title="metadataOverride">
              <select
                style={sel}
                value={metaMode}
                onChange={(e) => setMetaMode(e.target.value as ExportPreset['metadata'] | '')}
              >
                <option value="">{t('metaFromPreset')}</option>
                <option value="keepAll">{t('metaKeepAll')}</option>
                <option value="stripGps">{t('metaStripGps')}</option>
                <option value="copyrightOnly">{t('metaCopyrightOnly')}</option>
                <option value="stripAll">{t('metaStripAll')}</option>
              </select>
            </Section>
            {error && <p style={{ color: 'var(--danger)' }}>{error}</p>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
              <button onClick={onClose}>{t('cancel')}</button>
              <button
                onClick={() => void run()}
                disabled={!scoped.length}
                data-testid="export-run"
                style={{ background: 'var(--accent-fill)', color: 'var(--on-accent)' }}
              >
                {t('exportNow')}
              </button>
            </div>
          </>
        )}
        {running && progress && (
          <div data-testid="export-progress">
            <progress max={progress.total} value={progress.done} style={{ width: '100%' }} />
            <p>
              {t('progress', { done: progress.done, total: progress.total })}
              {progress.etaMs !== null && progress.etaMs > 0
                ? ` · ${t('eta', { s: Math.ceil(progress.etaMs / 1000) })}`
                : ''}
            </p>
            <p className={s.muted} style={{ wordBreak: 'break-all' }}>
              {progress.current}
            </p>
            <button
              disabled={cancelling}
              onClick={() => {
                signal.current.cancelled = true;
                setCancelling(true);
              }}
            >
              {cancelling ? t('cancelling') : t('cancel')}
            </button>
          </div>
        )}
        {result && (
          <div data-testid="export-summary">
            <p>
              {result.cancelled
                ? t('batchCancelled', { n: result.written.length })
                : t('batchDone', { n: result.written.length, s: (result.ms / 1000).toFixed(1) })}
            </p>
            {result.failures.length > 0 && (
              <>
                <p style={{ color: 'var(--danger)' }}>{t('batchFailures', { n: result.failures.length })}</p>
                <ul style={{ maxHeight: 160, overflow: 'auto', fontSize: 12 }}>
                  {result.failures.map((f, i) => (
                    <li key={i} title={f.reason}>
                      {f.photo} ·{' '}
                      {f.contactSheet
                        ? t('contactSheet')
                        : lang === 'es' && f.presetEs
                          ? f.presetEs
                          : f.preset}
                      : {t(f.reasonKey)}
                    </li>
                  ))}
                </ul>
              </>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={onClose}>{t('close')}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
